import React from 'react';
import { Sparkles, ArrowRight, Brain, Target, ArrowLeft, Play, TrendingUp, ShoppingCart } from 'lucide-react';

interface HeroProps {
  onStartQuiz: () => void;
  onBackToWaitlist: () => void;
  hasProfile?: boolean;
  onGoToDashboard?: () => void;
  onGoToShop?: () => void;
}

export default function Hero({ onStartQuiz, onBackToWaitlist, hasProfile = false, onGoToDashboard, onGoToShop }: HeroProps) {
  const handleExploreShop = () => {
    if (onGoToShop) {
      onGoToShop();
    } else {
      console.warn('onGoToShop function not provided to Hero component');
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Back to Landing Button */}
      <button
        onClick={onBackToWaitlist}
        className="fixed top-4 left-4 sm:top-6 sm:left-6 z-50 bg-black/50 backdrop-blur-md border border-pink-500/30 text-pink-400 px-3 py-2 sm:px-4 sm:py-2 rounded-full font-medium hover:bg-pink-500/10 transition-all duration-300 flex items-center space-x-2 text-sm sm:text-base"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="hidden sm:inline">Back to Landing</span>
        <span className="sm:hidden">Back</span>
      </button>

      {/* Profile Status Indicator */}
      {hasProfile && (
        <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-50 bg-green-500/20 backdrop-blur-md border border-green-500/30 text-green-400 px-3 py-2 sm:px-4 sm:py-2 rounded-full font-medium flex items-center space-x-2 text-sm sm:text-base">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span>DrizzID™ Active</span>
        </div>
      )}

      {/* Enhanced Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/20 via-purple-600/30 to-blue-600/20 animate-pulse"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(236,72,153,0.15),transparent_50%)]"></div>
      
      {/* Floating sauce drops */}
      <div className="absolute top-20 left-10 w-2 h-2 sm:w-3 sm:h-3 bg-pink-500 rounded-full animate-pulse"></div>
      <div className="absolute top-40 right-20 w-3 h-3 sm:w-4 sm:h-4 bg-purple-500 rounded-full animate-bounce"></div>
      <div className="absolute bottom-40 left-20 w-1 h-1 sm:w-2 sm:h-2 bg-pink-400 rounded-full animate-ping"></div>
      <div className="absolute top-1/3 right-1/3 w-4 h-4 sm:w-6 sm:h-6 bg-gradient-to-br from-pink-500/30 to-purple-600/30 rounded-full blur-sm animate-pulse"></div>
      
      <div className="relative z-10 text-center max-w-6xl mx-auto px-4 sm:px-6">
        {/* Enhanced Logo */}
        <div className="mb-6 sm:mb-8">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full blur-xl sm:blur-2xl opacity-50 animate-pulse"></div>
            <img 
              src="/logo transparent with tagline.png" 
              alt="UNDRESSING Logo" 
              className="relative h-20 sm:h-24 lg:h-32 w-auto mx-auto mb-4 sm:mb-6 object-contain"
            />
          </div>
        </div>

        {/* Main Heading */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-black mb-3 sm:mb-4">
            <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-pink-600 bg-clip-text text-transparent">
              DRIZZ
            </span>
            <span className="text-white">ID™</span>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-2">
            AI-Powered Algorithmic Taste Profiling
          </p>
          <p className="text-xs sm:text-sm text-pink-400 font-semibold tracking-wide">
            BY UNDRESSING SAUCES
          </p>
        </div>

        {/* Profile Status Message */}
        {hasProfile && (
          <div className="mb-6 sm:mb-8 bg-green-500/10 border border-green-500/20 rounded-xl p-4 sm:p-6">
            <div className="flex items-center justify-center space-x-3 mb-3">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-400 font-semibold">Your DrizzID™ Profile is Active</span>
            </div>
            <p className="text-gray-300 text-sm sm:text-base">
              You can view your dashboard, retake the quiz to update your profile, or explore new features.
            </p>
          </div>
        )}

        {/* Features Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-8 sm:mb-12">
          {[
            { icon: Brain, label: 'AI Analysis', desc: 'Neural taste mapping' },
            { icon: Target, label: 'Precision', desc: 'Molecular profiling' },
            { icon: Sparkles, label: 'Discovery', desc: 'Infinite possibilities' },
            { icon: ArrowRight, label: 'Evolution', desc: 'Adaptive learning' }
          ].map((feature, index) => (
            <div key={index} className="bg-white/5 backdrop-blur-sm border border-pink-500/20 rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 hover:bg-white/10 transition-all duration-300 group">
              <feature.icon className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-pink-400 mx-auto mb-2 sm:mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="text-white font-semibold text-sm sm:text-base mb-1">{feature.label}</h3>
              <p className="text-gray-400 text-xs sm:text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8 sm:mb-16">
          {hasProfile ? (
            <>
              <button
                onClick={onGoToDashboard}
                className="w-full sm:w-auto bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg hover:shadow-2xl hover:shadow-green-500/25 transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2"
              >
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>View My Dashboard</span>
              </button>
              <button
                onClick={onStartQuiz}
                className="w-full sm:w-auto border border-pink-500/50 text-pink-400 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-pink-500/10 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <Brain className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>Retake Quiz</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onStartQuiz}
                className="w-full sm:w-auto bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg hover:shadow-2xl hover:shadow-pink-500/25 transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Build Your DrizzID™</span>
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <button 
                onClick={handleExploreShop}
                className="w-full sm:w-auto border border-pink-500/50 text-pink-400 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-pink-500/10 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>Explore Sauces</span>
              </button>
            </>
          )}
        </div>

        {/* Animated Analytics Preview */}
        <div className="grid grid-cols-3 gap-4 sm:gap-6 lg:gap-8 text-center">
          {[
            { number: 'AI', label: 'Powered Analysis', color: 'text-pink-400' },
            { number: '∞', label: 'Flavor Combos', color: 'text-purple-400' },
            { number: '24/7', label: 'Learning', color: 'text-cyan-400' }
          ].map((stat, index) => (
            <div key={index}>
              <div className={`text-2xl sm:text-3xl font-bold ${stat.color} animate-pulse`}>
                {stat.number}
              </div>
              <div className="text-gray-400 text-xs sm:text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}